import { Box, Flex, Grid } from '@/components/atoms/Grid'
import { InformationH2, Small, Small2 } from '@/components/atoms/typography'
import CategoryPathHeader from '@/components/molecules/categoryPathHeader'
import { IconButtonCapitalized } from '@/components/molecules/iconButtons'
import { Routes } from '@/core/routes'
import useTranslation from 'next-translate/useTranslation'
import styled, { css } from 'styled-components'
import { transparentize } from 'polished'
import PageContentWrapper, {
  PageContentWrapperBackgroundStyle
} from '@/components/organisms/pageContentWrapper'
import { validFor } from '@/theme/validFor'
import { FC, useEffect, useState } from 'react'
import ProductCard from '@/components/organisms/productCard/productCard'
import {
  ShowProductModal,
  withProductDetailPopup
} from '@/HOC/withProductDetailPopup'
import {
  getWishlistSSR,
  useWishlistDispatch
} from '@/providers/wishlistDispatchProvider'
import SharePopup from '@/components/organisms/sharePopup'
import { ProductCardMode } from '@/components/organisms/productCard/productCardMode'
import Confirm from '@/components/organisms/confirm'
import { BorderedButton } from '@/components/atoms/button'
import {
  GetSeoTagsQuery,
  Page,
  Product,
  Sdk,
  SharedWishlist
} from '@/network/graphql.g'
import useGraphQl from 'hooks/useGraphQl'
import { requestThrow401 } from '@/utils/graphQl'
import { isLoggedInBySession, useUser } from '@/providers/userProvider'
import { withBaseServerSideProps } from '@/HOC/withBaseServerSideProps'
import { useMhub } from '@/providers/mhubProvider'
import { getProductListingData } from '@/components/organisms/productListing/productListingRedesign'
import { getShallowParams } from '@/utils/routerHelpers'
import { useRouter } from 'next/router'
import { AtIconDefault } from '@/components/atoms/icons/AtIconDefault'
import { FacebookCircleIconDefault } from '@/components/atoms/icons/FacebookCircleIconDefault'
import { getMainData } from '@/utils/mainDataHelper'
import { isAvailable as isProductAvailable } from '@/network/models/product'
import { useSpeedCurve } from '@/core/speedCurve/useSpeedCurve'
import { useNavigationSource } from '@/providers/navigationSourceProvider'

type WishlistProps = {
  hash: string
  wishlistProducts: Product[]
  sharedWishlistProducts: SharedWishlist
  isShared: boolean
  showProductModal?: ShowProductModal
}

const Wishlist: FC<WishlistProps> = ({
  hash,
  wishlistProducts,
  sharedWishlistProducts,
  isShared,
  showProductModal
}) => {
  const { t } = useTranslation('common')
  const { isLoggedIn } = useUser()
  const { clearWishlist } = useWishlistDispatch()
  const [showSharePopup, setShowSharePopup] = useState<boolean>(false)
  const [showRemoveAllConfirm, setShowRemoveAllConfirm] =
    useState<boolean>(false)
  const { requestNotifySuccessError, requestNotifyError } = useGraphQl()
  const router = useRouter()

  const [data, setData] = useState<Product[]>(() => {
    if (!isShared) {
      return wishlistProducts ?? []
    } else {
      return sharedWishlistProducts?.products?.filter((item) => item) ?? []
    }
  })

  const [isWishlistItemRemoved, setWishlistItemRemoved] = useState<{
    [product: string]: boolean
  }>({})

  useEffect(() => {
    if (!isShared && wishlistProducts != null) {
      setData(wishlistProducts)
    }
  }, [wishlistProducts])

  useEffect(() => {
    // handled separately in wishlist
    if (router.query.na) {
      delete router.query.na
      const { url, as } = getShallowParams(router)
      router.replace(url, as, { shallow: true })
    }
  }, [])

  const title = isShared
    ? t('Wishlist.sharedTitle', {
        name: sharedWishlistProducts?.customer?.firstname ?? ''
      })
    : t('Wishlist.title')

  const shareEmail = async (email: string) => {
    await requestNotifySuccessError((sdk: Sdk) => ({
      method: sdk.sendWishlistByEmail,
      variables: {
        sendToEmail: email
      },
      successText: () => t('Wishlist.emailShareSuccess')
    }))
    setShowSharePopup(false)
  }

  const shareFacebook = async () => {
    const result = await requestNotifyError((sdk: Sdk) => ({
      method: sdk.shareWishlist
    }))
    if (result.success) {
      const href = `${window.location.href}/${result.response?.shareWishlist?.shareId}`
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          href
        )}`
      )
    }
  }

  const { pushWishlistPage } = useMhub()
  useEffect(() => {
    pushWishlistPage()
  }, [])

  useSpeedCurve({
    label: 'Campaign'
  })

  const { setNavigationSource } = useNavigationSource()

  useEffect(() => {
    setNavigationSource({
      from: 'wishlist',
      name: title
    })
  }, [])

  return (
    <PageContentWrapper
      backgroundStyle={PageContentWrapperBackgroundStyle.normal}
    >
      <CategoryPathHeader
        paths={[
          { url: `${Routes.wishlist}${hash ? `/${hash}` : ''}`, title: title }
        ]}
      />
      {showRemoveAllConfirm && (
        <Confirm
          title={t('Wishlist.textWantRemoveAllProducts')}
          onDidTapClose={() => setShowRemoveAllConfirm(false)}
          onSubmit={(result) => {
            if (result) {
              clearWishlist(data)
            }
            setShowRemoveAllConfirm(false)
            setData([])
          }}
        />
      )}
      <HeaderWrapper
        flexDirection={{ desktop: 'row', tablet: 'column', mobile: 'column' }}
      >
        <InformationH2>{title}</InformationH2>
        {isLoggedIn && !isShared && data.length > 0 && (
          <>
            <Flex
              flexDirection={{
                desktop: 'row',
                tablet: 'row',
                mobile: 'column'
              }}
              alignItems={{
                desktop: 'center',
                tablet: 'inherit',
                mobile: 'inherit'
              }}
            >
              <StyledSmall mr={30}>{t('Wishlist.showYourWishes')}</StyledSmall>
              <AtButton
                mr={{ desktop: 10, tablet: 10, mobile: 0 }}
                mb={{ desktop: 0, tablet: 0, mobile: 10 }}
                IconElement={<AtIconDefault />}
                title={t('Wishlist.shareByEmail')}
                onClick={() => {
                  setShowSharePopup(true)
                }}
              />
              <FacebookButton
                IconElement={<FacebookCircleIconDefault />}
                title={t('Wishlist.shareByFacebook')}
                onClick={shareFacebook}
              />
            </Flex>
          </>
        )}
        {data.length > 0 && !isShared && (
          <Box textAlign="right">
            <RemoveButton
              ml={{ desktop: 10, tablet: 0, mobile: 0 }}
              mt={{ desktop: 0, tablet: 10, mobile: 10 }}
              title={t('Wishlist.remove')}
              onClick={() => {
                setShowRemoveAllConfirm(true)
              }}
            />
          </Box>
        )}
      </HeaderWrapper>
      <Box mb={80} data-testid="productListingContainer">
        {data.length > 0 ? (
          <Grid
            mt={{ desktop: 10, tablet: 20, mobile: 30 }}
            gridTemplateColumns={{
              desktop: '1fr 1fr 1fr 1fr 1fr',
              mobile: '1fr 1fr',
              tablet: '1fr 1fr 1fr'
            }}
            gridRowGap={'30px'}
            gridColumnGap={'20px'}
          >
            {data.map((item, offset) => {
              const isAvailable = isProductAvailable(item)
              return (
                isWishlistItemRemoved[item.id] !== false && (
                  <ProductCard
                    key={item?.id}
                    mode={
                      isShared
                        ? ProductCardMode.productList
                        : ProductCardMode.wishlist
                    }
                    onAddToCart={showProductModal}
                    product={item}
                    position={offset + 1}
                    list={!isShared ? 'wishlist.personal' : 'wishlist.shared'}
                    isAvailable={isAvailable}
                    isShared={isShared}
                    setWishlistItemRemoved={setWishlistItemRemoved}
                  />
                )
              )
            })}
          </Grid>
        ) : (
          <StyledSmall2>{t('Wishlist.textEmpty')}</StyledSmall2>
        )}
      </Box>
      {showSharePopup && (
        <SharePopup
          onDidTapClose={() => setShowSharePopup(false)}
          onSubmit={shareEmail}
        />
      )}
    </PageContentWrapper>
  )
}

export default withProductDetailPopup(Wishlist)

const getSharedWishlistProducts = async (
  sdk: Sdk,
  hash: string
): Promise<SharedWishlist> => {
  try {
    return (
      await sdk.getSharedWishlistProducts({
        shareId: hash
      })
    )?.sharedWishlistProducts
  } catch {
    return null
  }
}

export const getServerSideProps = withBaseServerSideProps(
  async ({
    context,
    session,
    sdk,
    withMainNavigation,
    withStore
  }): Promise<GetServerSideProps<WishlistProps>> => {
    const productListingData = getProductListingData(context)
    if ('redirect' in productListingData) {
      return {
        redirect: productListingData.redirect
      }
    }

    const isShared =
      Boolean(context.params.hash) && context.params.hash.length > 0
    const hash = isShared ? context.params.hash[0] : null
    let sharedWishlistProducts: SharedWishlist = null
    let wishlistProducts: Product[] = null
    let response: GetSeoTagsQuery = null

    const requestPromise = requestThrow401({
      method: sdk.getSeoTags,
      variables: {
        page: Page.Wishlist,
        withMainNavigation,
        withStore
      }
    })

    if (isShared) {
      const sharedWishlistProductsPromise = getSharedWishlistProducts(sdk, hash)
      ;[{ response }, sharedWishlistProducts] = await Promise.all([
        requestPromise,
        sharedWishlistProductsPromise
      ])
    } else {
      const wishlistProductsPromise = getWishlistSSR(
        sdk,
        isLoggedInBySession(session),
        context
      )
      ;[{ response }, wishlistProducts] = await Promise.all([
        requestPromise,
        wishlistProductsPromise
      ])
    }

    return {
      props: {
        mainData: getMainData(response),
        seoTags: response?.seoTags,
        hash,
        isShared,
        sharedWishlistProducts,
        wishlistProducts,
        disableDaktela: true
      }
    }
  }
)

const HeaderWrapper = styled(Flex)`
  ${validFor.desktop`     
    align-items: center;
    justify-content: space-between;
  `};
`

const StyledSmall2 = styled(Small2)`
  opacity: 0.6;
`

const StyledSmall = styled(Small)`
  opacity: 0.6;
`

const StyledButton = css`
  align-items: center;
  text-transform: uppercase;
  padding: 0 25px;
  height: 38px;
`

const StyledIconButtonCapitalized = css`
  border: 0;
  ${StyledButton}
`

const AtButton = styled(IconButtonCapitalized)`
  ${StyledIconButtonCapitalized};
  background-color: ${(props) => props.theme.colors.onBackground};
  color: ${(props) => props.theme.colors.background};
  &:hover {
    background-color: ${(props) =>
      transparentize(0.2, props.theme.colors.onBackground)};
  }
`

const FacebookButton = styled(IconButtonCapitalized)`
  ${StyledIconButtonCapitalized};
  background-color: ${(props) => props.theme.colors.facebook};
  color: ${(props) => props.theme.colors.background};
  &:hover {
    background-color: ${(props) =>
      transparentize(0.2, props.theme.colors.facebook)};
  }
`

const RemoveButton = styled(BorderedButton)`
  ${StyledButton};
  & ${Small} {
    font-size: 12px;
    font-weight: 500;
  }
  ${validFor.mobile`
    width: 100%;
  `}
`
